import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, CheckCircle2, ChevronRight, Clock, CornerDownLeft, Flag, ListTree, Loader2, Repeat, Send, Tag } from 'lucide-react';
import BotAvatar from '../bots/BotAvatar';
import DictateButton from '../bots/DictateButton';
import { AttachButton, IMAGE_TYPES, MAX_BYTES, MAX_FILES, PendingFiles } from '../bots/ChatFiles';
import { useBotLabel } from '../bots/botDisplay';
import { useDefaultChatBot } from '../bots/defaultBot';
import { chatPath } from '../bots/chatLinks';
import { primaryBtn } from '../bots/cards/CardShell';
import { ProjectDot } from '../layout/SidebarParts';
import { useLabelsQuery, useMyBotsQuery, useProjectSectionsQuery, useProjectsQuery, useTeamsQuery } from '../../hooks/queries';
import { useAuth } from '../../contexts/AuthContext';
import { attachmentsApi, botsApi } from '../../services/api';
import { clipboardFiles, isFilePaste } from '../../lib/clipboardFiles';
import { useCreateBotChat, useCreateTask, useOpenQueen, useSendBotMessage } from '../../hooks/mutations';
import { useDateTime } from '../../contexts/DateTimeContext';
import { useTask } from '../../contexts/TaskContext';
import { durationLabel } from '../../desktop/taskText';
import { cn } from '../../lib/utils';
import { dayWords, parseQuickInput } from './quickParse';
import { peopleOf, useTokenSuggest } from './useTokenSuggest';
import { botOf, botsFor, taskFields, uniqueLabels } from './taskFromParse';

// BusyBee herself (backend agents/queen.py) before she is made: POST /bots/queen on first use.
const QUEEN = { id: 'queen', name: 'BusyBee', queen: true, owner_type: 'user' };

/** The typed text read as a task (components/create/quickParse.js), in the app's language,
 * with what "#", "@", "/" and "+" name: the projects, the target project's people and bots,
 * its lists, the labels. `read(line)` reads another line the same way (several lines at once). */
export const useQuickParse = (text, project = null) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { data: projects } = useProjectsQuery();
  const { data: teams } = useTeamsQuery();
  const { data: labelsData } = useLabelsQuery();
  const { data: mine } = useMyBotsQuery();
  const words = useMemo(() => dayWords(i18n.language, t), [i18n.language, t]);
  const all = useMemo(() => (projects || []).filter((p) => !p.deleted_at), [projects]);
  const target = useMemo(() => parseQuickInput(text, { projects: all, words }).project || project, [text, all, words, project]);
  const { data: sectionsData } = useProjectSectionsQuery(target?.id);
  return useMemo(() => {
    const labels = uniqueLabels(Array.isArray(labelsData) ? labelsData : [], user?.id);
    const sectionsOf = (tp) => (tp && tp.id === target?.id && Array.isArray(sectionsData) ? sectionsData.filter((x) => !x.deleted_at) : []);
    const context = (tp) => ({ projects: all, words, labels, sections: sectionsOf(tp),
      people: [...peopleOf(tp, teams), ...botsFor(tp, mine, user?.id)] });
    const here = context(target);
    const read = (line) => parseQuickInput(line, context(parseQuickInput(line, { projects: all, words }).project || project));
    // The lists the suggestions offer (not what the text names: that is section / labels).
    return { ...parseQuickInput(text, here), projects: all, teams: teams || [], people: here.people,
      sectionOptions: here.sections, labelOptions: labels, read };
  }, [text, all, teams, labelsData, mine, user, words, target, sectionsData, project]);
};

/** What the full task form starts with for the typed text (TaskContext openTaskCreation). */
export const taskFormPrefill = (parsed, text, project, sectionId) => {
  const target = parsed.project || project || null;
  return {
    ...(text.trim() ? { title: (parsed.title || text).trim() } : {}),
    ...(target ? { projectId: target.id } : {}),
    ...(sectionId && target && target.id === project?.id ? { sectionId } : {}),
    ...(parsed.date ? { date: parsed.date } : {}),
    ...(parsed.time ? { startTime: parsed.time } : {}),
    ...(parsed.duration ? { duration: parsed.duration } : {}),
  };
};

const chipClass = 'inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-full border border-border text-[12.5px] text-foreground max-w-full';
const iconTile = 'w-9 h-9 rounded-lg bg-brand-soft text-brand-icon flex items-center justify-center flex-shrink-0';

const Option = ({ active, icon, title, subtitle, trailing, onClick, disabled, testId }) => (
  <button type="button" role="option" aria-selected={active} onClick={onClick} disabled={disabled} data-testid={testId}
    // The typing stays in the field: a click on an option does not take the focus away first.
    onMouseDown={(e) => e.preventDefault()}
    // The chosen one is plain to see: filled, with a honey edge (and its Add / Send).
    className={cn('w-full flex items-center gap-3 px-2.5 py-2 rounded-lg border text-left transition-colors disabled:opacity-60',
      active ? 'bg-accent border-amber-500/60' : 'border-transparent hover:bg-accent/60')}>
    {icon}
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-medium text-foreground truncate">{title}</span>
      {subtitle && <span className="block text-xs text-muted-foreground truncate mt-0.5">{subtitle}</span>}
    </span>
    {trailing}
  </button>
);

/**
 * The one input: "Ask BusyBee or add a task". What is typed is either a task (a day, a
 * time, #project, p1..p4: added at once, details come after) or a question for BusyBee
 * (a new chat with her; in a project with the bot used there last). Both choices show as
 * two rows under the field; the one the text looks like comes first and Enter takes it,
 * the arrow keys switch. Used by New (Ctrl+K, the sidebar's "+ New", the phone's "+") and
 * on Home. variant: dialog (desktop New), sheet (phone New) or home (Home's composer,
 * the rows show once something is typed). In a project (`project`, `sectionId`) a task
 * lands there and the chat is the project's.
 */
const QuickInput = ({ project = null, sectionId = null, variant = 'dialog', value, onValueChange, autoFocus, onDone, className }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const botLabel = useBotLabel();
  const { openTaskDetail } = useTask();
  const { toLocalDateString, toUTCISO, timeFormat } = useDateTime();
  const [own, setOwn] = useState('');
  const text = value ?? own;
  const setText = (next) => { if (onValueChange) onValueChange(next); else setOwn(next); };
  const parsed = useQuickParse(text, project);
  const field = useRef(null);
  const suggest = useTokenSuggest({ value: text, setValue: setText, inputRef: field,
    projects: parsed.projects, teams: parsed.teams, people: parsed.people, sections: parsed.sectionOptions, labels: parsed.labelOptions });
  const home = variant === 'home';
  const sheet = variant === 'sheet';

  // Where it goes: the "#project" typed, else the project this is opened in.
  const target = parsed.project || project || null;
  // Who answers: the default bot of a new chat there (components/bots/defaultBot.js): the bot
  // used there last (or its first), else BusyBee. BusyBee is made when the question is sent.
  const { bot: defaultBot } = useDefaultChatBot(target, { makeQueen: false });
  const askBot = defaultBot || QUEEN;
  const chatProject = target && !askBot.queen ? target : null;  // BusyBee's chats are in no project

  // Enter takes what the text looks like; an arrow key picks the other until that changes.
  const [picked, setPicked] = useState(null);
  const auto = parsed.isTask ? 'task' : 'ask';
  const choice = picked && picked.when === parsed.isTask ? picked.kind : auto;
  // Before anything is typed nothing is chosen, unless the person tapped a row.
  const typed = !!text.trim();
  const shown = typed || (picked && picked.when === parsed.isTask) ? choice : null;
  const pick = (kind) => {
    if (typed) { run(kind); return; }
    setPicked({ kind, when: parsed.isTask });
    field.current?.focus();
  };
  const order = auto === 'task' ? ['task', 'ask'] : ['ask', 'task'];

  const createTask = useCreateTask();
  const openQueen = useOpenQueen();
  const createChat = useCreateBotChat();
  const send = useSendBotMessage();
  const [busy, setBusy] = useState(null);  // 'task' | 'ask' while it is sent
  // Files that go with it (a screenshot of a bug): picked, pasted or dropped, shown before it is
  // sent and removable; uploaded when sent - to the task made, or with the question to the bot.
  const [files, setFiles] = useState([]);
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => () => filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), []);
  const addFiles = (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const room = MAX_FILES - filesRef.current.length;
    if (picked.length > room) toast.error(t('bots.files.max', { count: MAX_FILES }));
    const ok = picked.slice(0, Math.max(0, room)).filter((file) => {
      if (file.size <= MAX_BYTES) return true;
      toast.error(t('bots.files.too_big', { name: file.name }));
      return false;
    });
    setFiles((all) => [...all, ...ok.map((file) => ({
      key: `${Date.now()}-${Math.random()}`, file, name: file.name || 'file', size: file.size, progress: 1, id: 'local',
      preview: IMAGE_TYPES.has(file.type) ? URL.createObjectURL(file) : null,
    }))]);
    field.current?.focus();
  };
  const removeFile = (key) => {
    setFiles((all) => {
      const gone = all.find((x) => x.key === key);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return all.filter((x) => x.key !== key);
    });
    field.current?.focus();  // typing and Enter go on in the field, not on the next file's X
  };
  const [dragging, setDragging] = useState(false);
  const dropProps = {
    onDragOver: (e) => { if ([...(e.dataTransfer?.types || [])].includes('Files')) { e.preventDefault(); setDragging(true); } },
    onDragLeave: () => setDragging(false),
    onDrop: (e) => {
      setDragging(false);
      if (e.dataTransfer?.files?.length) { e.preventDefault(); addFiles(e.dataTransfer.files); }
    },
  };

  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [text]);

  const finish = () => {
    setText('');
    setPicked(null);
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
    onDone?.();
  };
  // The files as a task's attachments (uploaded first, as in the task form): what could not be
  // uploaded is said, the rest goes on the task.
  const uploadForTask = async () => {
    if (!files.length) return [];
    const results = await Promise.allSettled(files.map((f) => attachmentsApi.upload(f.file)));
    const uploaded = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value?.data) uploaded.push(r.value.data);
      else if (r.reason?.response?.status === 402) toast.error(t('tasks.storage_limit_reached'));
      else toast.error(t('bots.files.failed', { name: files[i].name }));
    });
    return uploaded;
  };
  const failed = (err) => {
    // 402 (credits) and 428 (AI permission) open their own dialogs.
    if (![402, 428].includes(err?.response?.status)) toast.error(t('desktop.quick.error'));
  };

  // One task, or one a line when several lines were typed or pasted. Each reads the same
  // symbols (taskFromParse.taskFields); a bot named with "@" takes its task right after.
  const addTask = async () => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    const reads = lines.length > 1 ? lines.map((line) => [line, parsed.read(line)]) : [[text, parsed]];
    let last = null;
    let attachments = await uploadForTask();  // on the first task when several lines make several
    for (const [line, r] of reads) {
      const lineTarget = r.project || project || null;
      const data = { title: (r.title || line).trim().slice(0, 500), ...taskFields(r, { toLocalDateString, toUTCISO }) };
      if (attachments.length) { data.attachments = attachments; attachments = []; }
      if (!data.project_id && lineTarget) data.project_id = lineTarget.id;
      if (!data.section_id && sectionId && lineTarget?.id === project?.id) data.section_id = sectionId;
      last = await createTask.mutateAsync(data);
      if (botOf(r) && last?.id) await botsApi.assignTask(last.id, botOf(r));
    }
    if (reads.length > 1) {
      toast.success(t('nav.create.added_many', { count: reads.length }));
    } else {
      toast.success(target ? t('desktop.quick.added_to', { name: target.name }) : t('desktop.quick.added'), {
        description: (parsed.title || text).trim(),
        action: last?.id ? { label: t('common.open'), onClick: () => openTaskDetail(last.id) } : undefined,
      });
    }
    finish();
  };

  const ask = async () => {
    // In the project's chat the "#Project" is where it is, not part of the question; to BusyBee
    // (no project) it says which project the question is about.
    const tag = chatProject && parsed.projectToken
      ? new RegExp(`(^|\\s)#${parsed.projectToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, 'i') : null;
    const content = (tag ? text.replace(tag, ' ') : text).replace(/[ \t]+/g, ' ').trim();
    let botId = askBot.id;
    if (askBot.queen && (!botId || botId === QUEEN.id)) botId = (await openQueen.mutateAsync()).id;  // made the first time
    const chat = await createChat.mutateAsync({ botId, projectId: chatProject?.id || null });
    // The files go with the question, as from the chat's own composer.
    const fileIds = [];
    for (const f of files) {
      try {
        fileIds.push((await botsApi.uploadFile(botId, chatProject?.id || null, f.file)).data.id);
      } catch (err) {
        toast.error(err?.response?.status === 413 ? t('bots.files.too_big', { name: f.name }) : t('bots.files.failed', { name: f.name }));
      }
    }
    await send.mutateAsync({ chatId: chat.id, content, fileIds });
    finish();
    navigate(chatPath(chat.id), { state: { chatId: chat.id, botId, projectId: chatProject?.id || null } });
  };

  const run = async (kind) => {
    if (!text.trim() || busy) return;
    setBusy(kind);
    try {
      if (kind === 'task') await addTask(); else await ask();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(null);
    }
  };

  const onKeyDown = (e) => {
    if (e.nativeEvent?.isComposing) return;
    if (suggest.onKeyDown(e)) return;  // the "#" / "@" list has the keys while it is open
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      run(choice);
    } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && text && !text.includes('\n')) {
      e.preventDefault();
      setPicked({ kind: choice === 'task' ? 'ask' : 'task', when: parsed.isTask });
    }
  };

  // --- what the task would be -------------------------------------------------------------
  const timeText = (hhmm) => {
    if (timeFormat === '24h') return hhmm;
    const [h, m] = hhmm.split(':').map(Number);
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  };
  const dayText = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return t('common.today');
    if (diff === 1) return t('common.tomorrow');
    return d.toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' });
  };
  const when = parsed.date ? [dayText(parsed.date), parsed.time && timeText(parsed.time)].filter(Boolean).join(' ') : null;
  // The repeat in the app's words ("Every day", "Every week" + the day).
  const repeatText = (rule) => {
    if (rule.type === 'weekly' && rule.days_of_week?.length) {
      const day = new Date(2026, 0, 5 + rule.days_of_week[0]);  // Jan 5, 2026 is a Monday (0)
      return `${t('bots.repeat.weekly')} · ${day.toLocaleDateString(i18n.language, { weekday: 'long' })}`;
    }
    return t({ daily: 'bots.repeat.daily', weekdays: 'bots.repeat.weekdays', weekly: 'bots.repeat.weekly',
      biweekly: 'bots.repeat.type_biweekly', monthly: 'bots.repeat.monthly' }[rule.type] || 'tasks.repeat');
  };
  const chips = [
    when && <span key="when" className={chipClass}><CalendarDays className="w-3.5 h-3.5 text-brand-icon flex-shrink-0" />{when}</span>,
    target && (
      <span key="project" className={chipClass}><ProjectDot project={target} /><span className="truncate">{target.name}</span></span>
    ),
    parsed.projectToken && !parsed.project && (
      <span key="unknown" className={cn(chipClass, 'text-muted-foreground')}>#{parsed.projectToken} · {t('desktop.quick.project_unknown')}</span>
    ),
    parsed.person && (
      <span key="person" className={chipClass}>
        {parsed.person.bot ? <BotAvatar bot={parsed.person.bot} size={16} /> : (
          <span className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">
            {parsed.personToken.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="truncate">{parsed.personToken}</span>
      </span>
    ),
    parsed.section && (
      <span key="section" className={chipClass}><ListTree className="w-3.5 h-3.5 text-brand-icon flex-shrink-0" /><span className="truncate">{parsed.section.name}</span></span>
    ),
    ...(parsed.labels || []).map((l) => (
      <span key={`label-${l.id}`} className={chipClass}><Tag className="w-3.5 h-3.5 flex-shrink-0" style={{ color: l.color || undefined }} /><span className="truncate">{l.name}</span></span>
    )),
    parsed.recurrence && (
      <span key="repeat" className={chipClass}><Repeat className="w-3.5 h-3.5 text-brand-icon flex-shrink-0" />{repeatText(parsed.recurrence)}</span>
    ),
    parsed.personToken && !parsed.person && (
      <span key="person-unknown" className={cn(chipClass, 'text-muted-foreground')}>@{parsed.personToken}</span>
    ),
    parsed.priority && <span key="priority" className={chipClass}><Flag className="w-3.5 h-3.5 text-brand-icon" />{parsed.priority.toUpperCase()}</span>,
    parsed.duration && (
      <span key="duration" className={chipClass}>
        <Clock className="w-3.5 h-3.5 text-brand-icon" />
        {durationLabel(parsed.duration, { h: t('desktop.quick.unit_h'), min: t('desktop.quick.unit_min') })}
      </span>
    ),
  ].filter(Boolean);

  const trailing = (kind) => {
    if (busy === kind) return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" />;
    if (shown !== kind || !typed) {
      return sheet ? <ChevronRight className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" aria-hidden="true" /> : null;
    }
    return sheet
      ? <span className={cn(primaryBtn, 'h-9 px-4')}>{kind === 'task' ? t('desktop.quick.add') : t('desktop.quick.send')}</span>
      : <CornerDownLeft className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />;
  };
  const options = {
    task: (
      <Option key="task" active={shown === 'task'} disabled={!!busy} onClick={() => pick('task')} testId="quick-add-task"
        icon={<span className={iconTile}><CheckCircle2 className="w-[19px] h-[19px]" /></span>}
        title={t('nav.create.add_task')}
        subtitle={typed ? [(parsed.title || text).trim(), when].filter(Boolean).join(' · ')
          : target ? t('desktop.quick.saves_to', { name: target.name }) : t('desktop.quick.saves_to_inbox')}
        trailing={trailing('task')} />
    ),
    ask: (
      <Option key="ask" active={shown === 'ask'} disabled={!!busy} onClick={() => pick('ask')} testId="quick-ask"
        icon={<BotAvatar bot={askBot} size={36} />}
        title={t('nav.create.ask', { name: botLabel(askBot) })}
        subtitle={chatProject ? t('nav.create.ask_in', { name: chatProject.name }) : t('nav.create.ask_hint')}
        trailing={trailing('ask')} />
    ),
  };

  const input = (
    <textarea ref={field} rows={sheet ? 2 : 1} value={text} autoFocus={autoFocus}
      onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} {...suggest.bind}
      // A screenshot or a copied file goes along (attached), its name is not typed in.
      onPaste={(e) => { if (isFilePaste(e)) { e.preventDefault(); addFiles(clipboardFiles(e)); } }}
      placeholder={t('nav.create.placeholder')} aria-label={t('nav.create.placeholder')} data-testid="quick-input"
      className={cn('w-full resize-none bg-transparent outline-none text-foreground placeholder:text-muted-foreground leading-snug',
        sheet ? 'text-base' : 'text-[15px]')} />
  );

  if (home) {
    return (
      <div className={cn('rounded-xl border border-border bg-card p-3 pb-2.5 focus-within:border-amber-500/60 transition-colors',
        dragging && 'border-amber-500/60', className)} data-testid="home-composer" {...dropProps}>
        {files.length > 0 && <div className="-mx-3 -mt-3 mb-1"><PendingFiles items={files} onRemove={removeFile} /></div>}
        <div className="relative px-1 pt-0.5">{input}{suggest.popup()}</div>
        {chips.length > 0 && <div className="flex flex-wrap gap-1.5 px-1 pt-2">{chips}</div>}
        {typed && <div role="listbox" className="mt-2 pt-2 border-t border-border space-y-0.5">{order.map((k) => options[k])}</div>}
        <div className="flex items-center gap-2 pt-2">
          <AttachButton onPick={addFiles} disabled={!!busy || files.length >= MAX_FILES} />
          <DictateButton value={text} onChange={setText} disabled={!!busy} />
          {!typed && <span className="min-w-0 truncate text-xs text-muted-foreground">{t('nav.create.hint')}</span>}
          <button type="button" onClick={() => run(choice)} disabled={!typed || !!busy}
            className="ml-auto inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label={choice === 'task' ? t('nav.create.add_task') : t('nav.create.ask', { name: botLabel(askBot) })}
            data-testid="home-composer-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className={cn('rounded-xl border border-border bg-background p-3 focus-within:border-amber-500/60 transition-colors',
        dragging && 'border-amber-500/60')} {...dropProps}>
        {files.length > 0 && <div className="-mx-3 -mt-3 mb-1"><PendingFiles items={files} onRemove={removeFile} /></div>}
        <div className="relative">{input}{suggest.popup()}</div>
        <div className="flex items-end gap-2 pt-1.5">
          <div className="flex-1 min-w-0 flex flex-wrap gap-1.5">{chips}</div>
          <AttachButton onPick={addFiles} disabled={!!busy || files.length >= MAX_FILES} />
          <DictateButton value={text} onChange={setText} disabled={!!busy} />
        </div>
      </div>
      <div role="listbox" className="space-y-0.5">{order.map((k) => options[k])}</div>
    </div>
  );
};

export default QuickInput;
